import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import type { Candidate } from './AddSelection';
import { DestinationPicker } from './DestinationPicker';
import {
  catalogAdminKey,
  editPayload,
  isSelectionConflict,
  SelectionPage,
  SelectionRow,
  useCatalogAdminCapability,
} from './queries';
import { membershipOffsetLimit, membershipPageSize, validPriority, validPriorityReason } from './priorityOrder';

/** Hotel-centric view of the existing global memberships, independent of publication. */
export function HotelDestinations({ hotelId }: { hotelId: string }) {
  const { t } = useTranslation();
  const capability = useCatalogAdminCapability();
  const allowed = capability.data?.allowed === true && !!hotelId;
  const cache = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const [destination, setDestination] = useState<Candidate | null>(null);
  const [selected, setSelected] = useState<SelectionRow | null>(null);
  const [rank, setRank] = useState('0');
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState(false);
  const inFlight = useRef(false);
  const page = useQuery({
    queryKey: [...catalogAdminKey, 'hotel-destinations', hotelId, offset],
    queryFn: async () =>
      (
        await client.get<SelectionPage>('hotel-catalog-admin/hotel-memberships', {
          params: { hotel_id: hotelId, limit: membershipPageSize, offset },
        })
      ).data,
    enabled: allowed && !selected,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const relationship = useQuery({
    queryKey: [...catalogAdminKey, 'hotel-destination-check', hotelId, destination?.id],
    queryFn: async () =>
      (
        await client.get<SelectionPage>('hotel-catalog-admin/hotel-memberships', {
          params: { hotel_id: hotelId, geo_place_id: destination!.id, limit: 1, offset: 0 },
        })
      ).data,
    enabled: allowed && adding && !!destination && !selected,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const existing = relationship.data?.data[0];
  const ready = !!destination && relationship.isSuccess && !relationship.isFetching;
  const save = useMutation({
    mutationFn: async (row: SelectionRow) =>
      client.put(
        'hotel-catalog-admin/hotel-memberships',
        editPayload('hotel-memberships', row, !row.enabled, row.enabled ? row.rank! : Number(rank), reason),
      ),
    retry: false,
    onSuccess: async () => {
      setSelected(null);
      setAdding(false);
      setDestination(null);
      setOffset(0);
      setSaved(true);
      await cache.invalidateQueries(catalogAdminKey);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const choose = (row: SelectionRow) => {
    setSelected(row);
    setRank(String(row.rank));
    setReason('');
    setSaved(false);
    save.reset();
  };
  const locked = save.isLoading || save.isError;
  if (!allowed) return null;
  return (
    <Paper variant="outlined" sx={{ m: 3, p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">{t('hotelDestinations.title')}</Typography>
        <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
          {t('hotelDestinationMembership.reference', { id: hotelId })}
        </Typography>
        <Typography>{t('hotelDestinations.help')}</Typography>
        {saved && <Alert severity="success">{t('hotelDestinationMembership.saved')}</Alert>}
        {page.isError ? (
          <Alert severity="error">
            {t('hotelCatalogAdmin.loadError')}
            <Button onClick={() => page.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>
          </Alert>
        ) : page.isFetching ? (
          <Typography>{t('hotelCatalogAdmin.loading')}</Typography>
        ) : (
          <Stack spacing={1}>
            {page.data?.data.map((row) => (
              <Paper key={row.geo_place_id} variant="outlined" sx={{ p: 2 }}>
                <Typography>{row.geo_place_name}</Typography>
                <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                  {row.geo_place_id}
                </Typography>
                <Typography>
                  {t(row.enabled ? 'hotelDestinationMembership.included' : 'hotelDestinationMembership.disabled')}
                  {' · '}
                  {t('hotelDestinationOrder.priority')}: {row.rank}
                </Typography>
                <Button disabled={!!selected} onClick={() => choose(row)}>
                  {t(row.enabled ? 'hotelDestinationMembership.deactivate' : 'hotelDestinationMembership.reactivate')}
                </Button>
              </Paper>
            ))}
            {page.data?.data.length === 0 && <Typography>{t('hotelDestinations.empty')}</Typography>}
          </Stack>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            disabled={!!selected || page.isFetching || offset === 0}
            onClick={() => setOffset((v) => Math.max(0, v - membershipPageSize))}
          >
            {t('hotelCatalogAdmin.previous')}
          </Button>
          <Button
            disabled={
              !!selected ||
              page.isFetching ||
              page.isError ||
              !page.data?.meta.has_more ||
              offset >= membershipOffsetLimit
            }
            onClick={() => setOffset((v) => v + membershipPageSize)}
          >
            {t('hotelCatalogAdmin.next')}
          </Button>
        </Stack>
        {page.data?.meta.has_more && offset >= membershipOffsetLimit && (
          <Alert severity="info">{t('hotelDestinationOrder.pageLimit')}</Alert>
        )}
        {!adding ? (
          <Button
            disabled={!!selected}
            onClick={() => {
              setAdding(true);
              setDestination(null);
              setSaved(false);
            }}
          >
            {t('hotelDestinations.add')}
          </Button>
        ) : (
          <Stack spacing={1}>
            <DestinationPicker value={destination} disabled={!!selected} onChange={setDestination} />
            {destination && relationship.isFetching && <Typography>{t('hotelCatalogAdmin.loading')}</Typography>}
            {destination && relationship.isError && (
              <Alert severity="error">
                {t('hotelCatalogAdmin.loadError')}
                <Button onClick={() => relationship.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>
              </Alert>
            )}
            {ready && (
              <Typography>
                {t(
                  existing?.enabled
                    ? 'hotelDestinations.alreadyIncluded'
                    : existing
                      ? 'hotelDestinations.reactivateHelp'
                      : 'hotelDestinations.newHelp',
                )}
              </Typography>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                disabled={!!selected}
                onClick={() => {
                  setAdding(false);
                  setDestination(null);
                }}
              >
                {t('hotelCatalogAdmin.cancel')}
              </Button>
              <Button
                disabled={!!selected || !ready || existing?.enabled === true}
                onClick={() => {
                  if (!ready || !destination || existing?.enabled || selected) return;
                  choose(
                    existing ?? {
                      hotel_id: hotelId,
                      geo_place_id: destination.id,
                      geo_place_name: destination.label,
                      enabled: false,
                      rank: 0,
                      version: 0,
                      updated_at: '',
                    },
                  );
                }}
              >
                {t('hotelDestinations.review')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
      <Dialog
        open={!!selected}
        fullWidth
        onClose={() => {
          if (!locked) setSelected(null);
        }}
      >
        <DialogTitle>{t('hotelDestinations.review')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography sx={{ overflowWrap: 'anywhere' }}>{selected?.hotel_display_name ?? hotelId}</Typography>
            <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
              {hotelId}
            </Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }}>
              {t('hotelDestinationMembership.targetDestination', {
                destination: selected?.geo_place_name,
                id: selected?.geo_place_id,
              })}
            </Typography>
            <Alert severity="info">
              {t(
                selected?.enabled
                  ? 'hotelDestinationMembership.deactivateHelp'
                  : 'hotelDestinationMembership.includeHelp',
                { destination: selected?.geo_place_name },
              )}
            </Alert>
            <TextField
              label={t('hotelDestinationOrder.priority')}
              type="number"
              value={rank}
              disabled={locked || selected?.enabled}
              onChange={(e) => setRank(e.target.value)}
              inputProps={{ min: 0, max: 2147483647, step: 1 }}
              sx={{ '& input': { fontSize: 16 } }}
            />
            <TextField
              label={t('hotelCatalogAdmin.reason')}
              value={reason}
              disabled={locked}
              onChange={(e) => setReason(e.target.value)}
              inputProps={{ maxLength: 512 }}
              sx={{ '& input': { fontSize: 16 } }}
            />
            {save.isError && (
              <Alert severity="error">
                {t(
                  isSelectionConflict(save.error)
                    ? 'hotelCatalogAdmin.conflict'
                    : 'hotelDestinationOrder.saveUncertain',
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={locked} onClick={() => setSelected(null)}>
            {t('hotelCatalogAdmin.cancel')}
          </Button>
          {save.isError ? (
            <Button
              onClick={() => {
                setSelected(null);
                setAdding(false);
                setDestination(null);
                save.reset();
                cache.invalidateQueries(catalogAdminKey);
              }}
            >
              {t('hotelCatalogAdmin.reload')}
            </Button>
          ) : (
            <Button
              disabled={save.isLoading || !validPriority(rank) || !validPriorityReason(reason)}
              onClick={() => {
                if (
                  !selected ||
                  !allowed ||
                  inFlight.current ||
                  locked ||
                  !validPriority(rank) ||
                  !validPriorityReason(reason)
                ) {
                  return;
                }
                inFlight.current = true;
                save.mutate(selected);
              }}
            >
              {t(
                selected?.enabled
                  ? 'hotelDestinationMembership.deactivate'
                  : selected?.version === 0
                    ? 'hotelDestinationMembership.confirmAdd'
                    : 'hotelDestinationMembership.confirmReactivate',
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
